import { getReservationsByProductType } from 'utils/helpers';

const dataLayerName = 'Craft3EcomDataLayer';

const paymentType = (booking) => {
  const payment = booking.payments_attributes.filter((i) => i.success)[0];
  const payment_type_id = booking.payment_type_id || payment?.payment_type_id;

  if (!payment_type_id) {
    return null;
  }

  return booking.payment_types_attributes.find(
    (type) => type.id === payment_type_id
  ).name;
};

const productInformation = (reservation) => ({
  id: reservation.id,
  name: reservation.selection_name,
  sku: reservation.resource_id,
  price: reservation.gross_including_packaged_item_in_cents / 100.0,
  quantity: reservation.quantity || 1,
  category: reservation.category || reservation.product_type_id,
});

const bookingDataBlob = (booking) => ({
  visitorDemographicInfo: booking.country || booking.country_id,
  transactionId: booking.reference,
  transactionAffiliation: booking.web_site_name || 'internal',
  transactionTotal:
    (booking.gross_in_cents - booking.commission_in_cents) / 100.0,
  transactionTax: 0,
  transactionShipping: 0,
  transactionPaymentType: paymentType(booking),
  transactionProducts: booking.reservations_attributes.map(productInformation),
  clientType: booking.client_type_name,
  name: booking.reservations_attributes.map((r) => r.selection_name),
});

// Returns true only when the push actually reached the data layer. Callers use
// that to decide whether to mark the booking as tracked: window.dataLayer is
// absent whenever GTM has not loaded or is blocked, and returning silently in
// that case let a caller burn a one-shot claim on an event that was never sent
// (SBAU-611).
const sendBookingData = (booking) => {
  const dataLayer = bookingDataBlob(booking);

  if (window.dataLayer?.push === undefined) {
    return false;
  }

  // For some reason `TagManager.dataLayer(...)` is not pushing the event.
  // Thus, pushing with vanilla Google Api
  // https://support.google.com/tagmanager/answer/7679219?hl=en

  window.dataLayer.push({
    event: 'transaction-info',
    dataLayer,
    dataLayerName,
  });

  return true;
};

const sendBookYourTrip = (booking) => {
  const sections = ['ferry', 'accommodation', 'experience'];

  const dataLayer = {};
  sections.forEach((section) => {
    const sectionData = getReservationsByProductType(section, booking).map(
      (reservation) => productInformation(reservation)
    );

    dataLayer[section] = sectionData;
  });

  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'book-your-trip',
      dataLayer,
      dataLayerName,
    });
  }
};

const viewBookingManagement = () => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'view_booking_management',
    });
  }
};

const startBookingEdit = ({ booking_id, user_id }) => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'start_booking_edit',
      booking_id,
      user_id,
    });
  }
};

const changeFerryTimeDate = ({
  booking_id,
  user_id,
  old_departure,
  new_departure,
  old_return,
  new_return,
}) => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'change_ferry_time_date',
      booking_id,
      user_id,
      old_departure,
      new_departure,
      old_return,
      new_return,
    });
  }
};

const bookingEditCompleted = ({
  booking_id,
  user_id,
  total_passenger_count,
  total_vehicle_count,
}) => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'booking_edit_completed',
      booking_id,
      user_id,
      total_passenger_count,
      total_vehicle_count,
    });
  }
};

const bookingEditError = ({ booking_id, user_id, description }) => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'booking_edit_error',
      booking_id,
      user_id,
      description,
    });
  }
};

const sendSoldOutServiceClick = (tripId, date) => {
  if (window.dataLayer?.push !== undefined) {
    window.dataLayer.push({
      event: 'sold_out_service_click',
      dataLayer: {
        trip_id: tripId,
        date,
      },
      dataLayerName,
    });
  }
};

export default {
  sendBookingData,
  bookingDataBlob,
  sendBookYourTrip,
  viewBookingManagement,
  startBookingEdit,
  changeFerryTimeDate,
  bookingEditCompleted,
  bookingEditError,
  sendSoldOutServiceClick,
};
